import React, { useCallback, useState } from 'react';
import { NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { BACKEND_URL } from '../config';

export const ResizableImageComponent: React.FC<NodeViewProps> = ({
  node,
  updateAttributes,
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const [startWidth, setStartWidth] = useState(0);
  const [startX, setStartX] = useState(0);

  const onResizeStart = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsResizing(true);
    setStartX(e.clientX);
    setStartWidth(node.attrs.width ? parseInt(node.attrs.width) : 100);
  }, [node.attrs.width]);

  const onResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  const onResize = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;

      const currentX = e.clientX;
      const diffX = currentX - startX;
      const newWidth = Math.max(50, Math.min(100, startWidth + (diffX / 5)));

      updateAttributes({
        width: `${newWidth}%`,
      });
    },
    [isResizing, startX, startWidth, updateAttributes]
  );

  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', onResize);
      document.addEventListener('mouseup', onResizeEnd);
    }

    return () => {
      document.removeEventListener('mousemove', onResize);
      document.removeEventListener('mouseup', onResizeEnd);
    };
  }, [isResizing, onResize, onResizeEnd]);

  const handleAlign = (alignment: 'left' | 'center' | 'right') => {
    updateAttributes({ alignment });
  };

  // Process the image URL to ensure it's absolute
  const getImageUrl = (src: string) => {
    if (src.startsWith('data:') || src.startsWith('http')) {
      return src;
    }
    // Remove any leading slashes to avoid double slashes
    const cleanPath = src.replace(/^\/+/, '');
    return `${BACKEND_URL}/${cleanPath}`;
  };

  return (
    <NodeViewWrapper className="relative group">
      <div className={`text-${node.attrs.alignment || 'center'}`}>
        <div className="inline-block relative">
          <img
            src={getImageUrl(node.attrs.src)}
            alt={node.attrs.alt}
            style={{ width: node.attrs.width || '100%' }}
            className="rounded-xl max-h-[500px] object-contain"
          />
          <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 dark:bg-gray-800/90 rounded-lg shadow-lg p-1 flex gap-1">
            <button
              onClick={() => handleAlign('left')}
              className={`p-1 rounded ${
                node.attrs.alignment === 'left' ? 'bg-brand-navy text-white' : 'hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
              title="Align Left"
            >
              ←
            </button>
            <button
              onClick={() => handleAlign('center')}
              className={`p-1 rounded ${
                node.attrs.alignment === 'center' ? 'bg-brand-navy text-white' : 'hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
              title="Center"
            >
              ↔
            </button>
            <button
              onClick={() => handleAlign('right')}
              className={`p-1 rounded ${
                node.attrs.alignment === 'right' ? 'bg-brand-navy text-white' : 'hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
              title="Align Right"
            >
              →
            </button>
          </div>
          <div
            className="absolute bottom-2 right-2 w-4 h-4 cursor-se-resize opacity-0 group-hover:opacity-100 transition-opacity"
            onMouseDown={onResizeStart}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              className="w-4 h-4 bg-white/90 dark:bg-gray-800/90 rounded shadow-lg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 14h6m0 0v6m0-6L4 20M20 10h-6m0 0V4m0 6l6-6"
              />
            </svg>
          </div>
        </div>
      </div>
    </NodeViewWrapper>
  );
}; 